import {Film} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Checkbox
} from '@kroll-resolver-crisp/ui-components-react';
import {format} from 'date-fns';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {type ChannelVideo, type ModerationTask} from '@/api/moderationQueue';
import {type FlagColor} from '@/api/flags';
import {FlagPicker} from '@/components/flags/Flag';
import {DurationBadge, ItemMeta} from '@/components/shared/ItemMeta';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {getDeclaredLanguageSubtag} from '@/lib/itemMetadata';
import {languageLabel} from '@/lib/queueFilters';
import {useTranslatedText} from '@/store/translationStore';

export const MAX_EVIDENCE_VIDEOS = 5;

// A channel's videos, by what the risk engine made of them.
const VIDEO_GROUPS: ReadonlyArray<{label: string; matches: (video: ChannelVideo) => boolean}> = [
  {label: 'Violative', matches: (video) => video.is_strike},
  {label: 'Non-violative', matches: (video) => !video.is_strike && video.is_assessed},
  {label: 'Not yet assessed', matches: (video) => !video.is_strike && !video.is_assessed}
];

// Applications that refer items for review (PRI.Ingress's ExternalIngest:Sources), by the name analysts
// know them by. An unlisted source shows as sent.
const REFERRAL_SOURCE_LABELS: Readonly<Record<string, string>> = {
  'nile-ssh': 'Nile SSH',
  'nile-gvve': 'Nile GVVE'
};

function referralSourceLabel(source: string): string {
  return REFERRAL_SOURCE_LABELS[source] ?? source;
}

type Props = {
  readonly task: ModerationTask;
  // Reviewing channels: their videos are listed for the analyst to pick as evidence.
  readonly channelQueue: boolean;
  readonly evidenceItemIds: ReadonlySet<string>;
  readonly onToggleEvidence: (externalId: string) => void;
  // The analyst copied the original's URL to review it on the platform: reported with the decision.
  readonly onOpened: () => void;
  readonly onFlag: (color: FlagColor | null) => void;
};

// The item under review: what it is, why the engine flagged it, and what the previous analyst said
// if it was sent here. Mount it with key={task.id} so a revealed thumbnail never carries over.
export function QueueItemPanel({
  task,
  channelQueue,
  evidenceItemIds,
  onToggleEvidence,
  onOpened,
  onFlag
}: Props) {
  const title = useTranslatedText(task.id, task.title);
  const isChannel = task.content_type === 'actor';
  const language = getDeclaredLanguageSubtag(task.metadata);

  return (
    <div className="flex min-w-0 flex-col gap-4 p-5">
      <div className="group border-border bg-muted relative aspect-video max-h-[46vh] w-full overflow-hidden border">
        {task.thumbnail_url ? (
          <SensitiveMedia src={task.thumbnail_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Film className="text-muted-foreground size-10" />
          </div>
        )}
        <DurationBadge
          metadata={task.metadata}
          className="absolute right-2 bottom-2 px-1.5 py-0.5 text-xs tabular-nums"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary">{isChannel ? 'Channel' : 'Video'}</Badge>
          <Badge variant="secondary">{task.suggested_risk ?? 'Unclassified'}</Badge>
          <Badge variant="secondary">{languageLabel(language)}</Badge>
          {task.is_reclassified ? <Badge variant="outline">Reclassified</Badge> : null}
          {task.referral_source ? <Badge variant="outline">Referred</Badge> : null}
        </div>
        <h2 className="font-heading text-base leading-snug font-medium">
          {title ?? task.external_id}
        </h2>
        <p className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs">
          <span>{task.author_name ?? task.author_handle ?? 'Unknown Channel'}</span>
          <span>·</span>
          <span className="font-mono">{task.external_id}</span>
          {task.published_at ? (
            <>
              <span>·</span>
              <span>Published {format(new Date(task.published_at), 'd MMM yyyy')}</span>
            </>
          ) : null}
          {task.url ? (
            <>
              <span>·</span>
              <CopyableUrl url={task.url} className="min-w-0" onCopied={onOpened} />
            </>
          ) : null}
        </p>
        <ItemMeta metadata={task.metadata} itemKey={task.id} title={task.title} />
        <div className="flex flex-wrap items-center gap-2">
          <FlagPicker
            variant="button"
            label="Flag"
            subject={task.title ?? task.external_id}
            value={task.flag}
            className="h-8"
            onChange={onFlag}
          />
        </div>
      </div>

      <section className="border-border bg-card flex flex-col gap-2 border p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium">Why this was flagged</h3>
          {isChannel || task.referral_only ? null : (
            <span className="text-muted-foreground text-xs">
              Engine confidence{' '}
              <strong className="text-foreground tabular-nums">
                {Math.round(task.confidence * 100)}%
              </strong>
            </span>
          )}
        </div>
        {task.referral_source ? (
          <p className="text-muted-foreground text-xs">
            <strong className="text-foreground">
              Referred from {referralSourceLabel(task.referral_source)}:
            </strong>{' '}
            {task.referral_reason ?? 'No reason given.'}
          </p>
        ) : null}
        {/* A referral-only task's rationale only repeats the referral above. */}
        {task.rationale && !task.referral_only ? (
          <blockquote className="border-border bg-muted/50 border-l-2 px-3 py-2 text-xs/relaxed italic">
            {task.rationale}
          </blockquote>
        ) : null}
        {task.reclassification_note ? (
          <p className="text-muted-foreground text-xs">
            <strong className="text-foreground">Reclassification note:</strong>{' '}
            {task.reclassification_note}
          </p>
        ) : null}
      </section>

      {channelQueue && task.channel_videos.length > 0 ? (
        <div className="border-border space-y-3 border p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Channel videos</p>
            <span className="text-muted-foreground text-xs">
              {evidenceItemIds.size} of {MAX_EVIDENCE_VIDEOS} selected
            </span>
          </div>
          {VIDEO_GROUPS.map(({label, matches}) => {
            const videos = task.channel_videos.filter(matches);
            return videos.length > 0 ? (
              <section key={label} aria-label={label} className="space-y-2">
                <h4 className="text-muted-foreground text-xs">
                  {label} ({videos.length})
                </h4>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {videos.map((video) => (
                    <div
                      key={video.id}
                      className="border-border bg-muted/40 flex items-center gap-2 border p-2">
                      <Checkbox
                        aria-label={`Select ${video.title ?? video.external_id}`}
                        checked={evidenceItemIds.has(video.external_id)}
                        disabled={
                          !evidenceItemIds.has(video.external_id) &&
                          evidenceItemIds.size >= MAX_EVIDENCE_VIDEOS
                        }
                        onCheckedChange={() => {
                          onToggleEvidence(video.external_id);
                        }}
                      />
                      <div className="group relative h-10 w-14 shrink-0 overflow-hidden">
                        {video.thumbnail_url ? (
                          <SensitiveMedia
                            compact
                            src={video.thumbnail_url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Film className="text-muted-foreground size-5" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-xs font-medium">
                          {video.title ?? video.external_id}
                        </span>
                        {video.published_at ? (
                          <span className="text-muted-foreground text-xs">
                            {format(new Date(video.published_at), 'd MMM yyyy')}
                          </span>
                        ) : null}
                      </div>
                      {video.is_strike ? (
                        <>
                          <Badge variant="outline" className="shrink-0">
                            Strike
                          </Badge>
                          {video.strike_risk_type ? (
                            <Badge variant="secondary" className="max-w-40 shrink-0 truncate">
                              {video.strike_risk_type}
                            </Badge>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            ) : null;
          })}
        </div>
      ) : null}

      {task.other_verticals.length > 0 ? (
        <Alert>
          <AlertTitle>Also reviewed for other policy violations</AlertTitle>
          <AlertDescription>
            <ul className="space-y-1">
              {task.other_verticals.map((other) => (
                <li key={other.policy_violation}>
                  <strong className="text-foreground">{other.policy_violation}</strong>:{' '}
                  {other.risks.join(', ')} - {other.analyst_email},{' '}
                  {new Date(other.created_at).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
